import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ForwardedRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type Ref,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn.ts'

export interface SelectOption<TValue extends string = string> {
  value: TValue
  label: string
  /**
   * Offered but not choosable. The option stays visible on purpose — hiding it
   * answers "why is this missing?" with silence, which is the question a
   * disabled row exists to answer.
   */
  disabled?: boolean
  /** A second line under the label — normally *why* a disabled option is disabled. */
  hint?: string
}

interface SelectProps<TValue extends string = string> {
  value: TValue
  onValueChange: (value: TValue) => void
  options: SelectOption<TValue>[]
  placeholder?: string
  className?: string
  triggerClassName?: string
  disabled?: boolean
}

interface Coords {
  /** Anchored from the top (open downward) or bottom (flipped upward). */
  top?: number
  bottom?: number
  right: number
  width: number
  maxHeight: number
}

const LISTBOX_GAP = 4
const LISTBOX_MIN_WIDTH = 160
const LISTBOX_MAX_HEIGHT = 256 // matches the `max-h-64` cap
const LISTBOX_MIN_HEIGHT = 120 // don't flip into a sliver

/**
 * Generic single-value dropdown.
 *
 * The listbox is portal-rendered to `document.body` so it never gets clipped
 * by an ancestor `overflow: hidden` container — `Dialog` body and scrollable
 * Card surfaces being the load-bearing cases. Position is measured from the
 * trigger's `getBoundingClientRect()`; the listbox stays right-aligned with
 * the trigger (matches the original absolute-positioned `right-0` behaviour).
 *
 * Keyboard semantics — opening focuses the currently-selected option (or the
 * first option when nothing is selected); Arrow Up/Down cycles, Home/End jump
 * to the ends, Enter / Space activate the focused option (native button
 * behaviour), Escape closes and restores focus to the trigger. Selecting any
 * option — by mouse or keyboard — also restores focus, so the next Tab lands
 * on the next form field rather than `<body>`.
 *
 * Scroll on the listbox itself does NOT close the menu (so the user can
 * scroll-wheel through a long option list); scroll on any *other* ancestor
 * does close it (matches "moved away → cancel intent", same intuition as the
 * sibling `Tooltip` primitive).
 */
function SelectInner<TValue extends string = string>(
  {
    value,
    onValueChange,
    options,
    placeholder,
    className,
    triggerClassName,
    disabled,
  }: SelectProps<TValue>,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<Coords | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const listboxRef = useRef<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null)
      return
    }
    const measure = () => {
      const node = triggerRef.current
      if (!node) return
      const r = node.getBoundingClientRect()
      // Flip upward when there isn't room below and there's more room above —
      // otherwise the fixed-positioned listbox spills past the viewport bottom
      // with no way to scroll to the lower options.
      const spaceBelow = window.innerHeight - r.bottom - LISTBOX_GAP
      const spaceAbove = r.top - LISTBOX_GAP
      const openUp = spaceBelow < LISTBOX_MAX_HEIGHT && spaceAbove > spaceBelow
      const space = openUp ? spaceAbove : spaceBelow
      const maxHeight = Math.max(LISTBOX_MIN_HEIGHT, Math.min(LISTBOX_MAX_HEIGHT, space))
      setCoords({
        ...(openUp
          ? { bottom: window.innerHeight - r.top + LISTBOX_GAP }
          : { top: r.bottom + LISTBOX_GAP }),
        // Clamp so an off-screen / narrow-viewport trigger can't push the
        // listbox past the right edge into negative offsets.
        right: Math.max(LISTBOX_GAP, window.innerWidth - r.right),
        width: r.width,
        maxHeight,
      })
    }
    measure()
    const onScroll = (e: Event) => {
      // Wheel-scroll inside the open listbox must not close it — only treat
      // scrolls on ancestors of the trigger (or anywhere else on the page)
      // as "user moved away".
      const t = e.target as Node | null
      if (listboxRef.current && t && listboxRef.current.contains(t)) return
      setOpen(false)
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', measure)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', measure)
    }
  }, [open])

  // Focus the currently-selected option (or the first one) on open. Deferred
  // one rAF so the portal subtree exists in the DOM by the time we query it.
  useLayoutEffect(() => {
    if (!open) return
    const raf = requestAnimationFrame(() => {
      const items = listboxOptions(listboxRef.current)
      if (items.length === 0) return
      const selectedIdx = items.findIndex((b) => b.getAttribute('aria-selected') === 'true')
      items[selectedIdx >= 0 ? selectedIdx : 0]?.focus()
    })
    return () => cancelAnimationFrame(raf)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node | null
      if (!t) return
      if (triggerRef.current?.contains(t)) return
      if (listboxRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const current = options.find((o) => o.value === value)

  const listboxStyle: CSSProperties =
    coords !== null
      ? {
          position: 'fixed',
          ...(coords.bottom !== undefined ? { bottom: coords.bottom } : { top: coords.top }),
          right: coords.right,
          minWidth: Math.max(coords.width, LISTBOX_MIN_WIDTH),
          maxHeight: coords.maxHeight,
        }
      : { position: 'fixed', visibility: 'hidden' }

  function selectOption(v: TValue) {
    onValueChange(v)
    setOpen(false)
    triggerRef.current?.focus()
  }

  function onListboxKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') {
      return
    }
    e.preventDefault()
    const items = listboxOptions(listboxRef.current)
    if (items.length === 0) return
    const active = document.activeElement as HTMLElement | null
    const idx = active instanceof HTMLButtonElement ? items.indexOf(active) : -1
    let next: number
    switch (e.key) {
      case 'ArrowDown':
        next = idx < items.length - 1 ? idx + 1 : 0
        break
      case 'ArrowUp':
        next = idx > 0 ? idx - 1 : items.length - 1
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = items.length - 1
        break
    }
    items[next]?.focus()
  }

  return (
    <div className={cn('relative inline-block', className)}>
      <button
        ref={(node) => {
          triggerRef.current = node
          if (typeof ref === 'function') ref(node)
          else if (ref) (ref as { current: HTMLButtonElement | null }).current = node
        }}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'flex h-9 min-w-[140px] items-center justify-between gap-2 rounded-md border border-border bg-input px-3 text-sm text-foreground',
          'outline-none transition-colors hover:border-border-hover',
          'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40',
          'disabled:cursor-not-allowed disabled:opacity-50',
          triggerClassName,
        )}
      >
        <span className={cn(!current && 'text-muted')}>{current?.label ?? placeholder ?? ''}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={cn('shrink-0 transition-transform', open && 'rotate-180')}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={listboxRef}
            role="listbox"
            tabIndex={-1}
            onKeyDown={onListboxKeyDown}
            style={listboxStyle}
            className="z-50 max-h-64 overflow-y-auto rounded-md border border-border bg-background-app py-1 shadow-lg outline-none"
          >
            {options.map((option) => {
              const isSelected = option.value === value
              const isDisabled = option.disabled === true
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={isDisabled || undefined}
                  disabled={isDisabled}
                  onClick={() => {
                    if (isDisabled) return
                    selectOption(option.value)
                  }}
                  className={cn(
                    'flex w-full flex-col items-start px-3 py-2 text-left text-sm transition-colors',
                    'outline-none focus-visible:bg-sidebar-hover-bg focus:bg-sidebar-hover-bg',
                    isDisabled && 'cursor-not-allowed opacity-50',
                    isSelected ? 'bg-primary-light text-foreground' : 'text-foreground',
                    !isDisabled && !isSelected && 'hover:bg-sidebar-hover-bg',
                  )}
                >
                  <span>{option.label}</span>
                  {option.hint !== undefined && (
                    <span className="mt-0.5 text-[11px] leading-tight text-muted">
                      {option.hint}
                    </span>
                  )}
                </button>
              )
            })}
          </div>,
          document.body,
        )}
    </div>
  )
}

/**
 * The options the keyboard may land on — disabled rows are visible but skipped.
 *
 * Focus-on-open and the arrow keys both read this list, so a listbox whose
 * first entry is disabled still opens on something choosable.
 */
function listboxOptions(node: HTMLDivElement | null): HTMLButtonElement[] {
  if (!node) return []
  return Array.from(node.querySelectorAll<HTMLButtonElement>('[role="option"]')).filter(
    (b) => b.getAttribute('aria-disabled') !== 'true',
  )
}

/*
 * `forwardRef` over a generic component requires a cast — TS can't preserve
 * the type parameter through `ForwardRefRenderFunction`. The runtime is the
 * generic inner function; the cast only fixes the public signature.
 */
export const Select = forwardRef(SelectInner) as <TValue extends string = string>(
  props: SelectProps<TValue> & { ref?: Ref<HTMLButtonElement> },
) => ReactElement
